/**
 * 路由守卫
 * 实现路由级权限验证和403错误处理
 */

import type { Router, RouteLocationNormalized } from 'vue-router'
import { permissionManager } from '@/utils/permission'
import { useUserStore } from '@/stores'
import { message } from 'ant-design-vue'
import { isAuthRoutePath } from '@/shared/api/l1-error-presenter'
import i18n from '@/i18n'

/**
 * 设置路由守卫
 */
export function setupRouterGuards(router: Router) {
  // 全局前置守卫
  router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore()

    if (to.meta.requiresAuth !== false) {
      if (!userStore.token) {
        next({
          path: '/login',
          query: { redirect: to.fullPath },
        })
        return
      }

      if (!userStore.userInfo) {
        try {
          await userStore.getUserInfo()
          if (userStore.userInfo) {
            permissionManager.setUser(userStore.userInfo)
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('获取用户信息失败:', error)
          }
          userStore.clearToken()
          if (!isAuthRoutePath(to.path)) {
            next({
              path: '/login',
              query: { redirect: to.fullPath },
            })
          } else {
            next()
          }
          return
        }
      }
    }

    // 检查路由权限
    if (!checkRouteAccess(to)) {
      // 无权限访问，跳转到403页面
      message.error('您没有权限访问此页面')
      next('/403')
      return
    }

    next()
  })

  // 全局后置守卫
  router.afterEach((to) => {
    const titleKey = to.meta.titleKey as string | undefined
    if (titleKey) {
      document.title = `${i18n.global.t(titleKey)} - CollabLingua`
    } else if (to.meta.title) {
      document.title = `${to.meta.title} - CollabLingua`
    } else {
      document.title = 'CollabLingua - 跨语种协同教研平台'
    }
  })
}

/**
 * 检查路由访问权限
 */
function checkRouteAccess(route: RouteLocationNormalized): boolean {
  // 如果路由不需要权限检查，直接允许访问
  if (route.meta.requiresAuth === false) {
    return true
  }

  // 认证页与错误页不需要权限检查
  if (isAuthRoutePath(route.path) || ['/403', '/404', '/500'].includes(route.path)) {
    return true
  }

  // 使用权限管理器检查路由权限
  return permissionManager.checkRoutePermission(route)
}

/**
 * 动态添加路由权限检查
 */
export function addRoutePermissionCheck(router: Router, routeName: string, permissions: string[]) {
  const route = router.getRoutes().find(r => r.name === routeName)
  if (route && route.meta) {
    route.meta.permissions = permissions
  }
}

/**
 * 检查当前路由是否可访问
 */
export function canAccessCurrentRoute(router: Router): boolean {
  const currentRoute = router.currentRoute.value
  return checkRouteAccess(currentRoute)
}